import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { Project } from '../types'
import { fmtMoney } from '../lib/format'
import ProgressBar from '../components/ProgressBar'

export default function FinancialsTab({ project }: { project: Project }) {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.financials>> | null>(null)
  const [forecast, setForecast] = useState<Awaited<ReturnType<typeof api.forecast.get>> | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    api.financials(project.id).then(setData).catch((e) => setErr((e as Error).message))
    api.forecast
      .get(project.id)
      .then(setForecast)
      .catch(() => setForecast(null))
  }, [project.id])

  if (err) return <div className="card alert">{err}</div>
  if (!data) return <p className="muted">Loading…</p>

  const { budget, change_orders, draws, heritage_revenue, qbo_actuals } = data
  const drawnPct = budget.with_approved_cos > 0 ? (budget.drawn_to_date / budget.with_approved_cos) * 100 : 0
  const gcPct = heritage_revenue.gc_fee_total > 0 ? (heritage_revenue.gc_fee_received / heritage_revenue.gc_fee_total) * 100 : 0

  return (
    <section>
      <h2>Financials — {data.project.name}</h2>
      <p className="muted small mb-3">
        Budget, change orders, draws, Heritage revenue. QBO actuals appear when the QBO integration
        is connected (Settings tab).
      </p>

      {forecast && (
        <div
          className="card mb-3"
          style={{
            borderLeft:
              forecast.health === 'red'
                ? '4px solid #cc4444'
                : forecast.health === 'yellow'
                ? '4px solid #d4af37'
                : '4px solid #2ea043',
          }}
        >
          <h3>
            Budget Forecast{' '}
            <span
              style={{
                fontSize: '0.75rem',
                background:
                  forecast.health === 'red'
                    ? '#cc4444'
                    : forecast.health === 'yellow'
                    ? '#d4af37'
                    : '#2ea043',
                color: '#fff',
                padding: '2px 8px',
                borderRadius: 3,
              }}
            >
              {forecast.health.toUpperCase()}
            </span>
          </h3>
          <div className="stat-grid">
            <Stat
              label="Projected final cost"
              value={fmtMoney(forecast.summary.projected_final_cost)}
            />
            <Stat
              label="Projected overrun"
              value={fmtMoney(forecast.summary.projected_overrun)}
              alert={forecast.summary.projected_overrun > 0}
            />
            <Stat
              label="Schedule complete"
              value={`${(forecast.summary.pct_schedule_complete * 100).toFixed(0)}%`}
            />
            <Stat
              label="Budget spent"
              value={`${(forecast.summary.pct_budget_spent * 100).toFixed(0)}%`}
            />
            <Stat
              label="Contingency remaining"
              value={fmtMoney(forecast.summary.contingency_remaining)}
              alert={forecast.summary.contingency_remaining < forecast.summary.contingency_total * 0.5}
            />
            {forecast.benchmarks.psf_projected != null && (
              <Stat
                label="$/sqft projected"
                value={`$${Math.round(forecast.benchmarks.psf_projected)}`}
              />
            )}
          </div>
          {forecast.warnings.length > 0 && (
            <div className="mt-2">
              <strong style={{ color: '#cc4444' }}>Warnings</strong>
              <ul>
                {forecast.warnings.map((w, i) => (
                  <li key={i} className="small">
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {forecast.phase_rollup.length > 0 && (
            <details className="mt-2">
              <summary className="muted small" style={{ cursor: 'pointer' }}>
                Per-phase variance ({forecast.phase_rollup.length})
              </summary>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Phase</th>
                    <th style={{ textAlign: 'right' }}>Budget</th>
                    <th style={{ textAlign: 'right' }}>Drawn</th>
                    <th style={{ textAlign: 'right' }}>Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {forecast.phase_rollup.map((p) => (
                    <tr key={p.phase} style={{ background: p.variance > 0 ? 'rgba(204,68,68,0.05)' : undefined }}>
                      <td>{p.phase}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoney(p.budgeted)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoney(p.drawn)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoney(p.variance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}

      <div className="card mb-3">
        <h3>Budget</h3>
        <div className="stat-grid mb-3">
          <Stat label="Original budget" value={fmtMoney(budget.original)} />
          <Stat label="With approved COs" value={fmtMoney(budget.with_approved_cos)} />
          <Stat label="Drawn to date" value={fmtMoney(budget.drawn_to_date)} />
          <Stat label="Remaining" value={fmtMoney(budget.remaining)} alert={budget.remaining < 0} />
        </div>
        <ProgressBar pct={drawnPct} />
        <p className="muted small mt-2">
          {drawnPct.toFixed(1)}% of budget drawn through SCS line items
        </p>
      </div>

      <div className="card mb-3">
        <h3>Change Orders</h3>
        <div className="stat-grid">
          <Stat label="Total COs" value={String(change_orders.total)} />
          <Stat label="Approved cost impact" value={fmtMoney(change_orders.approved_impact)} />
          <Stat
            label="Pending cost impact"
            value={fmtMoney(change_orders.pending_impact)}
            alert={change_orders.pending_impact > 0}
          />
        </div>
      </div>

      <div className="card mb-3">
        <h3>Draws</h3>
        <div className="stat-grid">
          <Stat label="Total draws" value={`${draws.total} of ${project.total_draws_allowed}`} />
          <Stat label="Funded total" value={fmtMoney(draws.funded_total)} />
          <Stat
            label="Construction loan"
            value={fmtMoney(project.construction_loan_amount)}
            sub={
              project.construction_loan_amount
                ? `${((draws.funded_total / project.construction_loan_amount) * 100).toFixed(0)}% drawn`
                : ''
            }
          />
        </div>
      </div>

      <div className="card mb-3">
        <h3>Heritage Revenue (GC fee)</h3>
        <div className="stat-grid mb-3">
          <Stat label="GC fee total" value={fmtMoney(heritage_revenue.gc_fee_total)} />
          <Stat label="Received" value={fmtMoney(heritage_revenue.gc_fee_received)} />
          <Stat label="Remaining" value={fmtMoney(heritage_revenue.gc_fee_remaining)} />
        </div>
        <ProgressBar pct={gcPct} />
        <p className="muted small mt-2">{gcPct.toFixed(0)}% of Heritage GC fee received</p>
      </div>

      <div className="card mb-3">
        <h3>QBO Actuals</h3>
        {qbo_actuals.tracked_total === 0 ? (
          <div className="callout small">
            No QBO transactions tagged to this project yet. Connect QBO in Settings, then hit "Sync now."
            Heritage Command matches transactions to projects by class/customer name (first word match
            against project name) and stores them here for budget-to-actual reconciliation.
          </div>
        ) : (
          <div className="stat-grid">
            <Stat label="Total tracked" value={fmtMoney(qbo_actuals.tracked_total)} />
            <Stat
              label="vs SCS drawn"
              value={fmtMoney(qbo_actuals.tracked_total - budget.drawn_to_date)}
              sub={
                qbo_actuals.tracked_total > budget.drawn_to_date
                  ? 'QBO sees more than SCS drawn — investigate'
                  : 'In sync'
              }
              alert={Math.abs(qbo_actuals.tracked_total - budget.drawn_to_date) > 5000}
            />
          </div>
        )}
      </div>
    </section>
  )
}

function Stat({
  label,
  value,
  sub,
  alert,
}: {
  label: string
  value: string
  sub?: string
  alert?: boolean
}) {
  return (
    <div className={`stat-card ${alert ? 'alert' : ''}`}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="muted small">{sub}</div>}
    </div>
  )
}
